import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Recherche as RechercheContrat } from '../lib/contrat';
import { useRequete } from '../lib/requete';
import { date } from '../lib/format';
import { ouvrirLien, urlConversationFront, type ContexteFront } from '../lib/front';
import { ErreurAffichee, Section, Squelette, Vide } from './ui';

/** Le contrat impose 400 ms au minimum : ne pas interroger l'API à chaque frappe. */
const DEBOUNCE_MS = 400;

/** En deçà, la requête ne discrimine rien et coûte un aller-retour pour rien. */
const LONGUEUR_MINIMALE = 3;

function useDebounce<T>(valeur: T, delai: number): T {
  const [retarde, setRetarde] = useState(valeur);

  useEffect(() => {
    const minuterie = window.setTimeout(() => setRetarde(valeur), delai);
    return () => window.clearTimeout(minuterie);
  }, [valeur, delai]);

  return retarde;
}

/**
 * Onglet « Recherche » : recherche plein texte française sur l'ensemble des
 * conversations, reclassée par le modèle côté pipeline.
 *
 * Tout le travail est côté serveur — le plugin envoie une question et affiche
 * un classement. Il ne reclasse rien lui-même et n'invente aucun score.
 */
export function Recherche({ contexteFront }: { contexteFront: ContexteFront | undefined }) {
  const [saisie, setSaisie] = useState('');
  const question = useDebounce(saisie.trim(), DEBOUNCE_MS);
  const interrogeable = question.length >= LONGUEUR_MINIMALE;

  const { donnees, erreur, chargement, rafraichir } = useRequete<RechercheContrat>(
    (signal) => api.recherche(question, 20, signal),
    [question],
    interrogeable,
  );

  return (
    <div className="pile">
      <label className="recherche-champ">
        <span className="visuellement-cache">Rechercher dans les conversations</span>
        <input
          type="search"
          value={saisie}
          onChange={(evenement) => setSaisie(evenement.target.value)}
          placeholder="palettisation de sacs lourds…"
          autoComplete="off"
          spellCheck={false}
        />
      </label>

      {!interrogeable && (
        <p className="aide">
          Posez la question en français, en toutes lettres. La recherche porte sur
          l’ensemble des conversations parcourues, pas seulement sur celle qui est
          ouverte.
        </p>
      )}

      {interrogeable && chargement && <Squelette lignes={4} />}
      {interrogeable && erreur && <ErreurAffichee erreur={erreur} onReessayer={rafraichir} />}

      {interrogeable && !chargement && !erreur && donnees && (
        <Resultats donnees={donnees} contexteFront={contexteFront} />
      )}
    </div>
  );
}

function Resultats({
  donnees,
  contexteFront,
}: {
  donnees: RechercheContrat;
  contexteFront: ContexteFront | undefined;
}) {
  if (donnees.resultats.length === 0) {
    return <Vide>Aucune conversation ne répond à cette question.</Vide>;
  }

  return (
    <Section
      titre={`${donnees.resultats.length} résultat${donnees.resultats.length > 1 ? 's' : ''}`}
      // La méthode vient du pipeline : elle dit à l'utilisateur ce qui a
      // réellement produit ce classement, plutôt que de le laisser deviner.
      aide={`${donnees.methode} · ${donnees.duree_ms} ms`}
    >
      <ul className="resultats">
        {donnees.resultats.map((resultat) => (
          <li key={`${resultat.cnv_id}-${resultat.sujet}`} className="resultat">
            <button
              type="button"
              className="resultat-lien"
              onClick={() => ouvrirLien(contexteFront, urlConversationFront(resultat.cnv_id))}
            >
              {resultat.sujet}
            </button>
            <p className="resultat-meta">
              {resultat.client && <span>{resultat.client}</span>}
              {resultat.date && <span>{date(resultat.date)}</span>}
              <span className="secondaire">pertinence {Math.round(resultat.pertinence * 100)} %</span>
            </p>
            <p className="resultat-extrait">{resultat.extrait}</p>
          </li>
        ))}
      </ul>
    </Section>
  );
}
